// ---------------------------------------------------------------------------
// What a counter is allowed to say when nothing measured it
//
// Two cards on two screens had the same defect for the same reason, so they now
// share one rule. It has exactly one job: refuse to print a number where a
// number would be a claim.
//
// FOUND 17 August. `bouncedAt` had exactly one writer — the Instantly sync poll
// — and the armed email path skips Instantly, so on every campaign sending
// through Unipile nothing had written that field for weeks. The card kept
// rendering, because it renders `campaign.totalBounced ?? 0`.
//
// So the screen answered a question nobody was measuring, with a number, in the
// same typeface as the numbers that are real. "We cannot see bounces" was
// displayed as "no bounces" — and of the two, the operator can only act on the
// second. That is the failure mode this module exists to make impossible: an
// unmeasured metric shown as zero is worse than showing nothing at all.
//
// A writer now exists on the Unipile path (`helpers/email-bounce.ts`), and it
// does not settle the question. It reads a delivery-status notice that happens
// to arrive in the connected mailbox; whether the far side sends one, and
// whether the provider delivers it to us, is not something this system can
// confirm. So on that path a recorded count is a FLOOR — real, and possibly
// short — while zero is simply an absence of evidence.
//
// Hence three readings rather than two, and the count alone can never
// distinguish them: only `bounceSource`, written by whichever path derived the
// counter, can.
//
// OPENS ARE THE SAME BUG WITH A DIFFERENT CAUSE, WHICH THE WORDING MUST KEEP
//
// `openedAt` has exactly one writer too — the same Instantly sync poll, reading
// `timestamp_last_open` — and the same armed path skips it. So `Opened` reads 0
// on every Unipile campaign, on the campaign screen AND on the dashboard.
//
// But where the missing bounce writer was an oversight, the missing open writer
// is a DECISION. `docs/unipile-email-api.md` records open tracking being
// hard-coded on under Instantly as a deliverability gap, and moving to Unipile
// as the moment that gap closes "by simply not setting it". An open is only
// observable by putting a tracking pixel in the mail; we deliberately do not.
//
// That difference has to survive into the tooltip, or the next person to read
// "not measured" fixes it by switching tracking on and quietly reopens a
// deliverability decision nobody asked to revisit. So an untracked counter says
// it is untracked ON PURPOSE, and says what it would cost to change.
// ---------------------------------------------------------------------------

export interface CounterReading {
  /** What the card prints. `'—'` when printing a number would be a claim. */
  value: number | string;
  /**
   * `measured` — the count is a reading and zero means zero.
   * `floor` — events have been recorded, but coverage is unconfirmed, so the
   *   true number is this or higher.
   * `unmeasured` — nothing has looked, or the only source cannot report an
   *   absence. Zero would be an invention.
   * `untracked` — nothing CAN look, by choice. Also not a zero, and the
   *   distinction from `unmeasured` is the point: one is a gap, the other is a
   *   decision, and they invite opposite responses.
   */
  kind: 'measured' | 'floor' | 'unmeasured' | 'untracked';
  /** Hover lines. Never empty for the readings that are not plain facts. */
  tooltip: string[];
}

/**
 * How to render the Bounced card for one campaign.
 *
 * Pure, and separated from the page so the rule can be tested without a DOM —
 * and so there is exactly one place that decides whether a zero may be printed.
 */
export function bounceReading(
  campaign: { totalBounced?: number; bounceSource?: string },
  /**
   * Bounces recorded on the LEADS the caller is holding right now — the count of
   * `lead.bouncedAt`. On the Unipile path the webhook writes that field the
   * moment a delivery-status notice arrives, but the campaign's `totalBounced`
   * aggregate only catches up on a sync — so a bounce could sit visible in the
   * Bounced filter while this card still read a stale zero and printed "—". The
   * fresher of the two wins, and the card can no longer disagree with the filter.
   */
  recordedFromLeads = 0,
): CounterReading {
  // The Instantly poll reads a bounce timestamp for every lead in the campaign,
  // so its zero is an observation like any other.
  if (campaign.bounceSource === 'instantly') {
    return { kind: 'measured', value: campaign.totalBounced ?? 0, tooltip: [] };
  }

  const count = Math.max(campaign.totalBounced ?? 0, recordedFromLeads);

  // A bounce that WAS recorded is a fact, whatever the coverage — print it, and
  // say in the tooltip that it may be short rather than pretending it is a
  // total.
  if (count > 0) {
    return {
      kind: 'floor',
      value: count,
      tooltip: [
        `${count} bounce${count === 1 ? '' : 's'} recorded — at least, not necessarily all`,
        'Bounces are read from delivery-status mail arriving in the sending mailbox. Not every dead address produces one, so treat this as a lower bound',
      ],
    };
  }

  // Nothing recorded, and nothing that could have recorded an absence.
  return {
    kind: 'unmeasured',
    value: '—',
    tooltip: [
      'Not measured — this is not a zero',
      'Bounces here are only seen if the receiving server sends a delivery-status notice back to the sending mailbox. Nothing polls for them, so "none recorded" cannot be read as "none happened"',
      'A recorded bounce would appear here as a number',
    ],
  };
}

/**
 * How to render the Opened card.
 *
 * NOT CURRENTLY RENDERED: both the campaign screen and the dashboard retired the
 * Opened card and show "Opted Out" instead — a metric that can only ever print
 * "—" (opens are untracked by design, see the header) was replaced by one that
 * actually moves. This is kept as the documented rule for if open tracking ever
 * returns; `tests/measured-counters.test.ts` pins that neither screen brings the
 * untracked zero back in the meantime.
 *
 * The difference from `bounceReading` is the last branch and only the last
 * branch. A bounce might arrive and simply has not; an open cannot arrive at
 * all, because the pixel that would report it is deliberately not in the mail.
 * Saying "not measured" there would be true and useless — it reads as a gap to
 * be closed, and closing it means re-enabling tracking.
 */
export function openReading(campaign: { totalOpened?: number; openSource?: string }): CounterReading {
  const count = campaign.totalOpened ?? 0;

  // Instantly's poll reads an open timestamp per lead, so its zero is a reading.
  if (campaign.openSource === 'instantly') {
    return { kind: 'measured', value: count, tooltip: [] };
  }

  // A campaign that ran through Instantly before this deployment moved. The
  // opens it recorded are real; what has stopped is anything adding to them.
  if (count > 0) {
    return {
      kind: 'floor',
      value: count,
      tooltip: [
        `${count} recorded while this campaign ran through Instantly`,
        'Nothing adds to it now — mail sent from here carries no tracking pixel, so later opens are not counted',
      ],
    };
  }

  return {
    kind: 'untracked',
    value: '—',
    tooltip: [
      'Not tracked — this is not a zero',
      'Knowing an email was opened means putting a tracking pixel in it. It was switched off on purpose when email moved off Instantly: pixels cost deliverability, and deliverability is what the whole campaign rides on',
      'So this is a decision, not a gap. Turning it back on is a product call, not a bug fix',
    ],
  };
}
